import { type LocalDate, crDateOf, daysBetween, formatCRC, Money } from "@despejo/domain";

/**
 * Reminder planning (plan §11.2, §34). Pure: given obligation state and
 * preferences, returns the notifications that should exist now. Each carries
 * a dedupe key, so running the planner any number of times per day produces
 * the same set (the database's unique dedupe_key absorbs repeats).
 *
 * Catch-up: reminders are bucketed. If the 7-day run was missed, the next run
 * still emits the "7 days" bucket (days 7..4), so a skipped cron never skips a
 * warning.
 */

export interface ReminderObligation {
  id: string;
  organization_id: string;
  taxpayer_id: string;
  taxpayer_name: string;
  obligation_code: string;
  name_es: string;
  form: string | null;
  period_label: string;
  due_date: LocalDate;
  filing_status: string;
  payment_status: string;
  preparation_status: string;
  has_payment: boolean;
  /** False for payment-only obligations (pagos parciales): nothing to file. Default true. */
  has_filing?: boolean;
  expected_amount: string | null;
  penalty_estimate: string | null;
  provisional: boolean;
}

export interface Recipient {
  user_id: string;
  email: string;
  reminder_days: number[];
  daily_overdue: boolean;
  email_enabled: boolean;
  escalation_email: string | null;
  escalation_after_days: number;
}

export interface NotificationDraft {
  organization_id: string;
  /** Null on digests spanning several taxpayers/obligations. */
  taxpayer_id: string | null;
  obligation_id: string | null;
  user_id: string | null;
  recipient: string;
  channel: "IN_APP" | "EMAIL";
  kind: string;
  dedupe_key: string;
  subject: string;
  body: string;
  severity: "INFO" | "WARNING" | "CRITICAL";
}

const FILED = new Set(["FILED", "FILED_LATE", "AMENDED", "NOT_APPLICABLE", "WAIVED"]);
const PAID = new Set(["PAID", "PAID_LATE", "NOT_DUE"]);

export type Pending = "FILING" | "PAYMENT" | null;

export function pendingAction(o: Pick<ReminderObligation, "filing_status" | "payment_status" | "has_payment" | "has_filing">): Pending {
  if (o.filing_status === "NOT_APPLICABLE" || o.filing_status === "WAIVED") return null;
  if (o.has_filing !== false && !FILED.has(o.filing_status)) return "FILING";
  if (o.has_payment && !PAID.has(o.payment_status) && o.payment_status !== "PAYMENT_ARRANGEMENT") return "PAYMENT";
  return null;
}

/** The reminder bucket for `daysLeft`, e.g. days=[30,14,7,3,1,0], daysLeft=5 -> 7. */
export function bucketFor(daysLeft: number, days: readonly number[]): number | null {
  const sorted = [...new Set(days)].filter((d) => d >= 0).sort((a, b) => a - b);
  const b = sorted.find((d) => d >= daysLeft);
  return daysLeft >= 0 && b !== undefined ? b : null;
}

const READINESS_ES: Record<string, string> = {
  UPCOMING: "aún no se ha empezado a preparar",
  COLLECTING_DATA: "se están recopilando los datos",
  DATA_INCOMPLETE: "faltan datos",
  READY_FOR_REVIEW: "está lista para revisión",
  READY_TO_FILE: "está lista para presentar",
};

function title(o: ReminderObligation): string {
  return `${o.name_es}${o.form ? ` (${o.form})` : ""} — ${o.period_label}`;
}

function money(v: string | null): string | null {
  return v === null ? null : formatCRC(Money.of(v));
}

export function planReminders(obligations: readonly ReminderObligation[], recipients: readonly Recipient[], now: Date): NotificationDraft[] {
  const today = crDateOf(now);
  const out: NotificationDraft[] = [];
  const digests = new Map<string, Digest>();

  for (const o of obligations) {
    const pending = pendingAction(o);
    if (!pending) continue;
    const daysLeft = daysBetween(today, o.due_date);
    const what = pending === "FILING" ? "presentar" : "pagar";
    const provisional = o.provisional ? "\nFecha provisional: la regla aún no ha sido confirmada contra la fuente oficial." : "";
    const readiness = pending === "FILING" ? `\nEstado de preparación: ${READINESS_ES[o.preparation_status] ?? o.preparation_status}.` : "";
    const amount = money(o.expected_amount);

    for (const r of recipients) {
      const base = { organization_id: o.organization_id, taxpayer_id: o.taxpayer_id, obligation_id: o.id, user_id: r.user_id };
      const channels: ("IN_APP" | "EMAIL")[] = r.email_enabled ? ["IN_APP", "EMAIL"] : ["IN_APP"];

      if (daysLeft >= 0) {
        const bucket = bucketFor(daysLeft, r.reminder_days);
        if (bucket === null) continue;
        const severity = daysLeft <= 3 ? "WARNING" : "INFO";
        const when = daysLeft === 0 ? "vence HOY" : daysLeft === 1 ? "vence mañana" : `vence en ${daysLeft} días (${o.due_date})`;
        const subject = `${o.taxpayer_name}: ${title(o)} ${when}`;
        const body =
          `${o.taxpayer_name} — ${title(o)}\nAcción pendiente: ${what}. ${when[0]!.toUpperCase()}${when.slice(1)}.` +
          readiness +
          (amount ? `\nMonto esperado: ${amount}.` : "") +
          (daysLeft === 0 && pending === "PAYMENT" ? "\nPague antes del corte de su banco, no a las 23:59." : "") +
          provisional;
        for (const channel of channels) {
          out.push({ ...base, recipient: channel === "EMAIL" ? r.email : r.user_id, channel, kind: `DUE_${bucket}`, dedupe_key: `${o.id}:${pending}:DUE_${bucket}:${channel}:${r.user_id}`, subject, body, severity });
        }
        continue;
      }

      // Overdue
      const late = -daysLeft;
      const penalty = money(o.penalty_estimate);
      const subject = `VENCIDO: ${o.taxpayer_name} — ${title(o)} (${late} ${late === 1 ? "día" : "días"} de atraso)`;
      const body =
        `${o.taxpayer_name} — ${title(o)} venció el ${o.due_date}.\n` +
        (pending === "FILING" ? "No hay comprobante de presentación registrado." : "No hay comprobante de pago registrado.") +
        (penalty ? `\nSanción estimada hoy: ${penalty} (estimación; el monto oficial lo determina Hacienda) y crece cada mes.` : "") +
        "\nSi ya lo presentó o pagó, suba el comprobante en Despejo." +
        provisional;
      if (r.daily_overdue) {
        // In-app: one per obligation. E-mail: one daily digest per recipient (built below), so a
        // taxpayer with years of unfiled history does not produce hundreds of e-mails a day.
        out.push({ ...base, recipient: r.user_id, channel: "IN_APP", kind: "OVERDUE", dedupe_key: `${o.id}:${pending}:OVERDUE:${today}:IN_APP:${r.user_id}`, subject, body, severity: "CRITICAL" });
        if (r.email_enabled) addDigest(digests, `${o.organization_id}|${r.user_id}|${r.email}`, { org: o.organization_id, user_id: r.user_id, to: r.email, escalation: false }, { o, late, what, penalty });
      }
      // Escalation contact (e.g. the accountant): one digest of everything overdue >= N days, sent the day an
      // obligation first reaches N days and otherwise on Mondays (never one e-mail per obligation).
      if (r.escalation_email && late >= r.escalation_after_days) {
        addDigest(digests, `${o.organization_id}|esc|${r.escalation_email}`, { org: o.organization_id, user_id: null, to: r.escalation_email, escalation: true }, { o, late, what, penalty }, late === r.escalation_after_days);
      }
    }
  }
  const monday = new Date(`${today}T12:00:00Z`).getUTCDay() === 1;
  for (const [key, d] of digests) if (!d.escalation || d.trigger || monday) out.push(digestDraft(key, d, today));
  return out;
}

interface DigestItem {
  o: ReminderObligation;
  late: number;
  what: string;
  penalty: string | null;
}

interface Digest {
  org: string;
  user_id: string | null;
  to: string;
  escalation: boolean;
  /** An item reached the escalation threshold today. */
  trigger: boolean;
  items: DigestItem[];
}

function addDigest(m: Map<string, Digest>, key: string, head: Omit<Digest, "items" | "trigger">, item: DigestItem, trigger = false): void {
  const d = m.get(key) ?? { ...head, trigger: false, items: [] };
  d.trigger ||= trigger;
  if (!d.items.some((x) => x.o.id === item.o.id)) d.items.push(item);
  m.set(key, d);
}

/** Lines listed in one overdue digest; the rest are summarised. Most recent first. */
export const DIGEST_LIST_MAX = 25;

function digestDraft(key: string, d: Digest, today: LocalDate): NotificationDraft {
  const items = [...d.items].sort((a, b) => a.late - b.late || a.o.taxpayer_name.localeCompare(b.o.taxpayer_name));
  const taxpayers = [...new Set(items.map((i) => i.o.taxpayer_id))];
  const n = items.length;
  const who = taxpayers.length === 1 ? ` de ${items[0]!.o.taxpayer_name}` : ` en ${taxpayers.length} contribuyentes`;
  const subject = `${d.escalation ? "[Escalamiento] " : ""}VENCIDO: ${n} ${n === 1 ? "obligación pendiente" : "obligaciones pendientes"}${who}`;
  const lines = items
    .slice(0, DIGEST_LIST_MAX)
    .map((i) => `- ${i.o.taxpayer_name} — ${title(i.o)}: ${i.what}; venció el ${i.o.due_date} (${i.late} ${i.late === 1 ? "día" : "días"})${i.penalty ? `; sanción estimada ${i.penalty}` : ""}`);
  const more = n > DIGEST_LIST_MAX ? `\n… y ${n - DIGEST_LIST_MAX} más en Despejo.` : "";
  const body =
    (d.escalation ? "Se le notifica como contacto de escalamiento.\n\n" : "") +
    `${n === 1 ? "Esta obligación está vencida" : "Estas obligaciones están vencidas"} y no tienen comprobante registrado:\n${lines.join("\n")}${more}\n\n` +
    "Las sanciones son estimaciones; el monto oficial lo determina Hacienda. Si ya presentó o pagó, suba el comprobante en Despejo o marque el historial al día.";
  return {
    organization_id: d.org,
    taxpayer_id: taxpayers.length === 1 ? taxpayers[0]! : null,
    obligation_id: n === 1 ? items[0]!.o.id : null,
    user_id: d.user_id,
    recipient: d.to,
    channel: "EMAIL",
    kind: d.escalation ? "ESCALATION_DIGEST" : "OVERDUE_DIGEST",
    dedupe_key: `${key}:${d.escalation ? "ESCALATION" : "OVERDUE"}_DIGEST:${today}`,
    subject,
    body,
    severity: "CRITICAL",
  };
}
